import TransportDocumentLinks from '../modules/transports/components/TransportDocumentLinks';
import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChartOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  IdcardOutlined,
  InboxOutlined,
  MoreOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  type MenuProps,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type TransportRouteDetailDto } from '../dto/transport';
import { canDeleteTransportRoute, canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { getTransportStatusDisplay, getTransportStatusDisplayTranslationKey } from '../modules/transports/transportRouteViews';
import { getTransportErrorMessage, isBlockingTransportError, TRANSPORT_ERROR_CODE_KEYS } from '../modules/transports/transportErrorMessages';
import BlockedActionsAlert from '../modules/warehouse/BlockedActionsAlert';
import { findBlockedAction, getBlockedActionMessage } from '../modules/warehouse/blockedActions';
import { useFeedback } from '../utils/feedback';
import { buildTransportRouteLabel } from '../modules/orders/transportAddressUtils';
import { authService } from '../services/auth';
import { formatDateEuropean } from '../utils/dateUtils';
import { formatCargoQuantitySummary } from '../utils/cargoQuantity';
import { formatMoneyPl } from '../utils/currency';
import { formatUnit } from '../utils/units';
import TransportStatusPicker from '../components/TransportStatusPicker';
import {
  findStaleCargoQuantityWarnings,
  formatStaleCargoQuantityWarningItems,
  getEffectiveTransportCargoItems,
  type SharedTransportCargoItem,
} from '../components/sharedTransportFormUtils';
import {
  calculateTransportCostAllocation,
  commercialAllocationMethodLabelKey,
  commercialAllocationWarningKey,
  DEFAULT_ALLOCATION_METHOD,
  type AllocationMethod,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';
import {
  DetailSection,
  RouteJourney,
  TransportStatusRail,
} from './TransportRouteDetailView';
// Object-card primitives (direction 2026-10-19) + document helpers; the stage
// rail (zk-rail) comes from the card skeleton stylesheet.
import { useBackNavigation } from '../hooks/useBackNavigation';
import { ObjectCardEmpty, ObjectCardFacts, ObjectCardHero, type ObjectCardFact } from '../components/layout/ObjectCard';
import '../components/layout/CardPage.css';
import '../components/layout/DocumentCard.css';
import './TransportRouteDetail.css';
import { TransportLinkedOrdersTable } from '../modules/transports/components/TransportLinkedOrdersTable';
import { TransportLoadTable } from '../modules/transports/components/TransportLoadTable';
import { buildTransportLoadTable } from '../modules/transports/transportLoadTable';
import { findShortDeliveries, toEffectiveCargoItem } from '../modules/transports/effectiveCargoQuantity';
import { getOrderProductDisplayDimensions } from '../modules/orders/orderProductDisplay';
import { TransportTrailerView } from '../modules/transports/components/TransportTrailerView';
import { TransportFreightKeyBar } from '../modules/transports/components/TransportFreightKeyBar';
import { previewRouteWithKey } from '../modules/transports/transportKeyPreview';
import { fetchSupplierOrder } from '../modules/orders/api/supplierOrdersApi';
import { buildTrailerLoad, trailerStopByOrderId, trailerToneByOrderId } from '../modules/transports/transportTrailerLoad';
import { buildDeliveryStops, deliveryStopNumberByOrderId } from '../modules/transports/transportDeliveryStops';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
  type TransportDeliveryFormItem,
} from '../modules/transports/components/TransportDeliveryQuantityFields';
import { unassignedSupplierLineMessages } from '../modules/transports/supplierCargoWarnings';
import { formatLocalizedExchangeRate, formatLocalizedNumber, formatLocalizedQuantity } from '../utils/numberFormatting';
import {
  hasCompleteTransportCostAllocation,
  hasKnownGoodsValue,
  hasUnknownLineAllocation,
} from '../modules/transports/transportCostAllocationPresentation';
import { readTransportReturnContext, resolveTransportReturnPath, withTransportReturnContext } from '../modules/transports/transportReturnContext';
import {
  deleteTransportRoute,
  fetchTransportRoute,
  unlinkTransportOrderAndRefetch,
  updateTransportRoute,
  updateTransportRouteAndRefetch,
} from '../modules/transports/api/transportRoutesApi';
import { showTransportSaveWarnings } from '../modules/transports/transportSaveWarnings';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { transportDatePickerProps } from '../components/transportFormUtils';
import { getTransportLabel } from '../utils/transportLabel';
import TransportKindTag from '../modules/transports/components/TransportKindTag';
import { buildUnlinkConfirmContent, buildUnlinkSuccessMessage } from '../modules/transports/transportUnlinkPreview';
import { defaultKeyLabel, defaultKeyLoadsOfRoute, routeAllocationKeyLabel } from '../modules/transports/transportCostSplitPreview';
import { deriveTransportKind } from '../modules/transports/transportDirection';
import { summarizeSupplierOrderFreight } from '../modules/transports/supplierOrderFreight';
import { summarizeCustomerOrderFreight } from '../modules/transports/customerOrderFreight';
import { truckSharesTotalPLN } from '../modules/transports/truckSharesPln';
import CustomerOrderFreightCards from '../modules/transports/components/CustomerOrderFreightCards';
import SupplierOrderFreightCards from '../modules/transports/components/SupplierOrderFreightCards';
import SupplierOrderRealizationCard from '../modules/transports/components/SupplierOrderRealizationCard';
import { summarizeSupplierOrderRealization } from '../modules/transports/supplierOrderRealization';
import { THOUSANDS_GROUPING } from '../utils/numberFormatting';
import TransportInvoiceDrawer from '../modules/transports/components/TransportInvoiceDrawer';
import TransportInvoiceSummary from '../modules/transports/components/TransportInvoiceSummary';
import { removeTransportInvoice, saveTransportInvoice, type TransportInvoicePayload } from '../modules/transports/api/transportRoutesApi';

const { TextArea } = Input;

interface TransportStatusUpdateValues {
  status: string;
  actualDelivery?: Dayjs | null;
  deliveryItems?: TransportDeliveryFormItem[];
  notes?: string;
}

type EffectiveCargoRow = SharedTransportCargoItem & { plannedQuantity?: number };

function getDetailCargoItemKey(item: SharedTransportCargoItem): string {
  return item.id || `${item.orderId}:${item.orderProductId || item.productName}`;
}

// Stage rail of the truck: the three display buckets of its status
// (modules/transports/transportRouteViews.tsx); cancelled is not a stage.
const RAIL_STEPS = ['planned', 'inProgress', 'completed'] as const;

const TransportRouteDetail: React.FC = () => {
  const { t, i18n } = useTranslation(['transport', 'common', 'products']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnContext = readTransportReturnContext(searchParams);
  const [route, setRoute] = useState<TransportRouteDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusForm] = Form.useForm();
  const editedStatus = Form.useWatch('status', statusForm);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canDeleteTransport = canDeleteTransportRoute(currentUser);
  // Backend-computed reasons (GET /transports/:id → blockedActions): settlement
  // basis, linked WZ/PZ. Buttons stay visible but disabled with the reason.
  const transportBlockedActions = route?.blockedActions || [];
  const transportBlockedFor = (action: string) => findBlockedAction(transportBlockedActions, action);
  const transportBlockedTooltip = (action: string) => {
    const blocked = transportBlockedFor(action);
    return blocked ? getBlockedActionMessage(blocked, t, 'transport', TRANSPORT_ERROR_CODE_KEYS) : undefined;
  };
  const canViewTransportCosts = authService.canViewTransportCosts();
  // Faktura przewoźnika: same gate as a rate edit (backend PUT /:id/invoice).
  const canEditTransportInvoice = canManageTransports && authService.canEditTransportCosts();
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);
  const [invoiceRemoving, setInvoiceRemoving] = useState(false);
  // Goods value on „Kalkulacja kosztu dostawy” is the ZK sale value: without
  // sale prices (Logistyk) the rows are left out, not shown as „—”.
  const canViewGoodsValue = authService.canViewCustomerSalePrices();

  useEffect(() => {
    if (!route) {
      return;
    }

    statusForm.setFieldsValue({
      status: route.status,
      actualDelivery: route.actualDelivery ? dayjs(route.actualDelivery) : null,
      deliveryItems: buildTransportDeliveryFormItems(route.cargoItems),
      notes: route.notes || undefined,
    });
  }, [route, statusForm]);

  // Freight key preview above the trailer (owner 2026-10-06): null = the saved key.
  const [previewKey, setPreviewKey] = useState<AllocationMethod | null>(null);
  const [keySaving, setKeySaving] = useState(false);
  // ZD line goods values (PLN) for the „wartość towaru” preview, cost roles only.
  const [zdLineValues, setZdLineValues] = useState<Map<string, number> | null>(null);

  const fetchRoute = useCallback(async (transportId: string) => {
    setLoading(true);

    try {
      setRoute(await fetchTransportRoute(transportId));
    } catch (error) {
      feedback.error(t('transport:errorFetchingRoute'));
      navigate('/app/transports');
    } finally {
      setLoading(false);
    }
  }, [navigate, t, feedback]);

  useEffect(() => {
    if (!id) {
      return;
    }

    void fetchRoute(id);
  }, [fetchRoute, id]);

  const linkedSupplierOrderIds = (route?.supplierOrders || []).map((supplierOrder) => supplierOrder.id).join(',');
  useEffect(() => {
    setZdLineValues(null);
    if (!linkedSupplierOrderIds || route?.direction !== 'INBOUND' || !canViewTransportCosts) return undefined;
    let cancelled = false;
    Promise.all(linkedSupplierOrderIds.split(',').map((supplierOrderId) => fetchSupplierOrder(supplierOrderId)))
      .then((supplierOrders) => {
        if (cancelled) return;
        const values = new Map<string, number>();
        supplierOrders.forEach((supplierOrder) => (supplierOrder.items || []).forEach((item) => {
          if (item.id && item.totalCostPLN != null) values.set(item.id, Number(item.totalCostPLN));
        }));
        setZdLineValues(values);
      })
      .catch(() => { /* the value preview stays unavailable */ });
    return () => { cancelled = true; };
  }, [linkedSupplierOrderIds, route?.direction, canViewTransportCosts]);

  const handleSaveFreightKey = async (key: AllocationMethod, keyLabel: string) => {
    if (!id || !route) return;
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('transport:freightKey.confirmTitle'),
        content: t('transport:freightKey.confirmContent', { key: keyLabel }),
        okText: t('transport:freightKey.confirmOk'),
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    setKeySaving(true);
    try {
      // Keep the PUT response: its save warnings (e.g. pairing, missing
      // dimensions) are shown like after a form save (review 2026-10-06).
      const saved = await submitWithOperationalException(
        async (confirmedPayload) => {
          const response = await updateTransportRoute(id, confirmedPayload);
          return { response, detail: await fetchTransportRoute(id) };
        },
        { costAllocationMethod: key },
        modal,
        t,
      );
      if (!saved) return;
      const refreshed = saved.detail;
      setRoute(refreshed);
      showTransportSaveWarnings(saved.response?.data, feedback, t, canViewTransportCosts);
      setPreviewKey(null);
      feedback.success(t('transport:freightKey.saved', { key: keyLabel, truck: getTransportLabel(refreshed) }));
    } catch (error: any) {
      feedback.error(getTransportErrorMessage(error, t, 'transport:errorUpdatingRoute', { number: getTransportLabel(route) }));
    } finally {
      setKeySaving(false);
    }
  };

  const handleSaveInvoice = async (payload: TransportInvoicePayload): Promise<boolean> => {
    if (!id || !route) return false;
    try {
      const refreshed = await saveTransportInvoice(id, payload);
      setRoute(refreshed);
      feedback.success(t('transport:invoice.saved', { number: payload.invoiceNumber, truck: getTransportLabel(refreshed) }));
      return true;
    } catch (error: any) {
      feedback.error(getTransportErrorMessage(error, t, 'transport:invoice.saveError', { number: getTransportLabel(route) }));
      return false;
    }
  };

  const handleRemoveInvoice = () => {
    if (!id || !route?.invoice) return;
    const invoiceNumber = route.invoice.number;
    // Without a plan the truck has no cost after the removal; say so.
    const hadPlan = route.invoice.plannedRate != null;
    modal.confirm({
      title: t('transport:invoice.removeTitle', { number: invoiceNumber }),
      content: t(hadPlan ? 'transport:invoice.removeContent' : 'transport:invoice.removeContentNoPlan'),
      okText: t('transport:invoice.remove'),
      okButtonProps: { danger: true },
      cancelText: t('common:cancel'),
      onOk: async () => {
        setInvoiceRemoving(true);
        try {
          const refreshed = await removeTransportInvoice(id);
          setRoute(refreshed);
          feedback.success(t(hadPlan ? 'transport:invoice.removed' : 'transport:invoice.removedNoPlan', {
            number: invoiceNumber, truck: getTransportLabel(refreshed),
          }));
        } catch (error: any) {
          feedback.error(getTransportErrorMessage(error, t, 'transport:invoice.removeError', { number: getTransportLabel(route) }));
        } finally {
          setInvoiceRemoving(false);
        }
      },
    });
  };

  const handleRemoveOrder = async (orderId: string) => {
    if (!id) {
      return;
    }
    // Round 2 (U11): confirm first; the success names the new amounts.
    // Usability round 2026-10-05: the confirmation already says who gets
    // how much after the unlink (transportUnlinkPreview).
    const removed = route?.routeOrders.find((row) => row.orderId === orderId);
    const removedName = removed?.order?.displayOrderNumber || removed?.order?.orderNumber || '';
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('transport:orderTransports.confirmUnlinkTitle'),
        content: buildUnlinkConfirmContent(canViewTransportCosts ? route : null, orderId, removedName, t),
        okText: t('transport:orderTransports.unlink'),
        okType: 'danger',
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    try {
      // WP12 (1.16): WZ pointing at this transport ask for a confirmed warning.
      const result = await unlinkTransportOrderAndRefetch(id, orderId, { modal, t });
      if ('cancelled' in result) return;

      if (result.deleted) {
        feedback.success(t('transport:orderRemovedSuccessfully', { number: getTransportLabel(route) }));
        navigate('/app/transports');
        return;
      }

      feedback.success(
        <span data-testid="transport-unlink-success">
          {buildUnlinkSuccessMessage(canViewTransportCosts ? result.route : null, removedName, getTransportLabel(result.route ?? route), t)}
        </span>,
        { duration: 12, key: 'transport-unlink-success' },
      );
      setRoute(result.route);
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorRemovingOrder', {
          number: getTransportLabel(route),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    }
  };

  // Owner request: the detail page had no whole-transport delete — only the
  // list page's row action. Same DELETE /transport-routes/:id endpoint and
  // canDeleteTransportRoute policy as the list, so the two surfaces can never
  // grant different access. WZ_TRANSPORT_LINK_EXISTS is mapped to a Polish
  // sentence naming the blocking documents via getTransportErrorMessage
  // instead of the raw English backend string.
  const handleDeleteTransport = async () => {
    if (!id) {
      return;
    }

    setDeleting(true);

    try {
      const deletedNumber = getTransportLabel(route);
      // WP12 (1.16): linked WZ/PZ ask for a confirmed warning; the links go
      // with the transport.
      const deleted = await deleteTransportRoute(id, { modal, t });
      if (!deleted) {
        setDeleteModalVisible(false);
        return;
      }
      feedback.success(t('transport:messages.deleteSuccess', { number: deletedNumber }));
      navigate('/app/transports');
    } catch (error: any) {
      // A delete blocked by a linked WZ/PZ names those documents — the operator
      // has to go unlink them, so the sentence must survive the click away.
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:messages.deleteError', {
          number: getTransportLabel(route),
        }),
        { sticky: isBlockingTransportError(error) },
      );
      setDeleteModalVisible(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdate = async (values: TransportStatusUpdateValues) => {
    if (!id) {
      return;
    }

    setSubmitting(true);

    try {
      const payload: Record<string, unknown> = {
        status: values.status,
        notes: values.notes,
        ...(values.status === 'DELIVERED'
          ? {
              actualDelivery: values.actualDelivery?.format('YYYY-MM-DD') || null,
              deliveryItems: values.deliveryItems || [],
            }
          : { actualDelivery: null }),
      };
      const refreshedRoute = await submitWithOperationalException(
        (confirmedPayload) => updateTransportRouteAndRefetch(id, confirmedPayload),
        payload,
        modal,
        t,
      );
      if (!refreshedRoute) return;
      feedback.success(t('transport:statusUpdatedSuccessfully', {
        number: getTransportLabel(route),
        status: t(getTransportStatusDisplayTranslationKey(values.status)),
      }));
      setStatusModalVisible(false);
      statusForm.resetFields();
      setRoute(refreshedRoute);
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorUpdatingStatus', {
          number: getTransportLabel(route),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Status pill of the card header: delivered = done, delayed = warning, cancelled = danger.
  const statusPill = (status: string) => {
    const display = getTransportStatusDisplay(status);
    const tone = status === 'DELAYED' ? 'warning'
      : display === 'completed' ? 'success'
        : display === 'cancelled' ? 'danger'
          : 'primary';
    return (
      <span className={`obj-pill obj-pill--${tone}`} data-testid="transport-detail-status">
        {t(getTransportStatusDisplayTranslationKey(status), { defaultValue: t('common:unknownStatus') })}
      </span>
    );
  };

  // S36/S37 (coordinator triage, 2026-07-07): the weight/volume/percent
  // columns below rendered raw `.toFixed()` output — "60.000" (dot decimal,
  // no thousands grouping) instead of Polish "60,000". Same pl-PL
  // formatting convention as formatCurrencyAmount below and
  // formatConversionValue (utils/dimensionConversion.ts) for m²/m³ — single
  // local helper so every metric cell on this page agrees, rather than each
  // column hand-rolling its own toFixed/toLocaleString call.
  const formatMetricNumber = (value: number | null | undefined, fractionDigits: number): string =>
    Number(value || 0).toLocaleString('pl-PL', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      ...THOUSANDS_GROUPING,
    } as Intl.NumberFormatOptions);

  if (loading || !route) {
    return (
      <div className="obj-card doc-card transport-detail">
        <div className="obj-loading"><Spin size="large" /></div>
      </div>
    );
  }

  // INBOUND truck: the freight belongs to the linked ZD (batch value), so the
  // customer-order allocations are 0 and the ZD lines are the physical load.
  // Backend facts only (supplierOrders[].items / freightShare) — see
  // modules/transports/supplierOrderFreight.ts.
  const supplierFreight = summarizeSupplierOrderFreight(route.supplierOrders);
  // OUTBOUND truck: each ZK's backend share of the cost (allocatedTransportCost)
  // over its cargo lines on this truck — see modules/transports/customerOrderFreight.ts.
  const customerFreight = summarizeCustomerOrderFreight(route.routeOrders, route.cargoItems);
  // The whole truck in PLN from the saved shares — one sum for the freight
  // cards, the key bar and the „Koszt” tile (modules/transports/truckSharesPln.ts).
  const truckTotalPLN = truckSharesTotalPLN({
    direction: route.direction,
    supplierOrderCount: route.supplierOrders.length,
    supplierTotalPLN: supplierFreight.totalAmountPLN,
    customerSharesPLN: customerFreight.rows.map((row) => row.share.amountPLN),
  });
  // Usability round 2026-10-05: line share of the whole truck and amounts in
  // the truck currency next to PLN (rate = the saved FX snapshot).
  const freightTruck = (() => {
    const currency = route.currency || 'PLN';
    const shareRate = (route.supplierOrders || []).find((row) => Number(row.freightShare?.exchangeRate) > 0)?.freightShare?.exchangeRate;
    const costRate = route.routeOrders.find((row) => Number(row.exchangeRateUsed) > 0)?.exchangeRateUsed;
    const rate = currency === 'PLN' ? 1 : (shareRate ?? costRate ?? null);
    return { currency, rate, totalPLN: truckTotalPLN };
  })();
  // INBOUND truck: Plan / Przyjęto / Nie przyjęto per ZD line from this truck's
  // received PZ (non-financial) — see modules/transports/supplierOrderRealization.ts.
  const supplierRealization = summarizeSupplierOrderRealization(route.supplierOrders);
  const hasCustomerOrders = route.routeOrders.length > 0;
  // A rate (or carrier invoice) in another currency gets its PLN equivalent;
  // a legacy truck without transportRate prints the PLN sum as its cost.
  const ratePLN = route.transportRate != null && (route.currency || 'PLN') !== 'PLN' ? truckTotalPLN : null;
  // „Po jakim kursie?”: the PLN line names the saved NBP rate and its table date.
  const ratePlnText = ratePLN === null ? null : route.freightExchangeRate
    ? t('transport:invoice.costRatePln', {
      amount: formatMoneyPl(ratePLN, 'PLN'),
      rate: formatLocalizedExchangeRate(route.freightExchangeRate.rate, 'pl'),
      date: route.freightExchangeRate.effectiveDate ? formatDateEuropean(route.freightExchangeRate.effectiveDate) : '—',
    })
    : `≈ ${formatMoneyPl(ratePLN, 'PLN')}`;
  const totalWeight = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.weight || 0), 0);
  const totalVolume = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.volume || 0), 0);
  // MED (m² exposure): match the form's "Przeliczniki ładunku" (m² / m³,
  // formatMetricsSummary in SharedTransportForm.tsx) whenever area is present. No legacy top-level route.totalAreaSqm column
  // exists (unlike totalWeight/totalVolume), so this sums the per-order
  // areaSqm the same way totalWeight/totalVolume above already do.
  const totalAreaSqm = hasCustomerOrders
    ? route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.areaSqm || 0), 0)
    : (supplierFreight.load.m2 ?? 0);
  const supplierLoadSummary = supplierFreight.load.pcs !== null
    ? `${formatMetricNumber(supplierFreight.load.pcs, 0)} ${formatUnit('pcs')}`
    : '';
  // A delivered truck's quantities are what it delivered (plan 2026-10-13).
  const customerCargoQuantitySummary = formatCargoQuantitySummary(
    getEffectiveTransportCargoItems(route).map((item) => toEffectiveCargoItem(item, route.status)),
    i18n.language,
  );
  const shortDeliveries = findShortDeliveries(route);
  const orderNumberById = new Map(route.routeOrders.map((row) => [row.orderId, row.order.displayOrderNumber || row.order.orderNumber]));
  const totalCargoQuantitySummary = !hasCustomerOrders && supplierFreight.hasLoad && supplierLoadSummary
    ? supplierLoadSummary
    : customerCargoQuantitySummary;
  // One key per truck (decision 2026-09-30): szt. / m² / m³ / wartość towaru / ręcznie.
  // Round 2 (U1): the default key names the measure it really uses, as in the form.
  const allocationKeyLabel = routeAllocationKeyLabel(route, t);
  const displayWeight = route.routeOrders.length > 0 ? totalWeight : route.totalWeight;
  const displayVolume: number | null = route.routeOrders.length > 0
    ? totalVolume
    : (route.totalVolume || supplierFreight.load.m3 || (supplierFreight.hasLoad ? null : 0));
  const effectiveCargoItems = getEffectiveTransportCargoItems(route).map((item) => toEffectiveCargoItem(item, route.status));
  // HOTEL (HIGH, "warn, never block"): a persisted cargo line's loaded
  // quantity can exceed the order product's CURRENT quantity when the order
  // was edited down after this transport was built. Non-blocking detection
  // only — no auto-resync, no save blocking. See findStaleCargoQuantityWarnings
  // for why no backend/DTO change was needed.
  const staleQuantityWarnings = findStaleCargoQuantityWarnings(route);
  // Legacy transports without stored cargo items get no (or an empty)
  // costAllocation from the API — recompute from the synthesized rows instead.
  const apiCostAllocation = route.costAllocation && route.costAllocation.items.length > 0
    ? route.costAllocation
    : null;
  // The goods value split is the server's (OPERATIONS has no prices): a legacy
  // truck without saved cargo rows under the value key shows "—", never a
  // client-side guess.
  const valueSplitPending = !apiCostAllocation && route.costAllocationMethod === 'BY_VALUE';
  const costAllocation = apiCostAllocation || calculateTransportCostAllocation({
    transportCost: route.transportRate ?? route.transportCost ?? 0,
    method: route.costAllocationMethod,
    items: effectiveCargoItems.map((item) => ({
      id: getDetailCargoItemKey(item),
      quantity: item.quantity,
      unit: item.unit,
      quantityPcs: item.quantityPcs,
      goodsValueTotal: item.goodsValueTotal,
      manualAllocatedTransportCost: item.manualAllocatedTransportCost,
      // Product identity + dimensions (mm) feed the m² / m³ keys of this
      // display-only fallback (legacy transports without cargo rows).
      name: item.productName,
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness,
      countInPiecesOnly: item.countInPiecesOnly === true,
    })),
  });
  const costAllocationItemsById = new Map(
    costAllocation.items.map((item) => [item.id, item]),
  );
  // MIXED_UNITS is a quantity-split-only advisory ("Podział według ilości może
  // być niepoprawny…"). A persisted allocation may still carry it even after the
  // operator moves the item-level split off BY_COMMERCIAL_QTY (to a metric/value
  // key that does not care about mixed commercial units), so only surface it
  // when the current item method is BY_COMMERCIAL_QTY.
  const visibleCostAllocationWarnings = costAllocation.warnings.filter(
    (warning) =>
      warning.code !== 'MIXED_UNITS' || route.costAllocationMethod === 'BY_COMMERCIAL_QTY',
  );
  // Server panel: line amounts = booked ZK share (null = cannot split); the
  // rest of the truck is the stock / ZD part. Local fallback (legacy truck
  // without cargo rows): the whole truck must split.
  const hasCompleteCostAllocation = apiCostAllocation
    ? !hasUnknownLineAllocation(apiCostAllocation)
    : !valueSplitPending && hasCompleteTransportCostAllocation(costAllocation);
  // The server panel is in PLN (each line is its ZK's booked PLN share,
  // utils/customerOrderLineFreight.js); the local fallback splits the rate in
  // the truck's own currency.
  const allocationCurrency = apiCostAllocation ? 'PLN' : route.currency;
  // Rest of the truck in PLN (stock / ZD part): the server compares the PLN
  // lines with the truck in PLN (backend utils/truckSharesPln.js); null when
  // it cannot say (a foreign-currency truck without readable shares).
  const truckRestPLN = apiCostAllocation ? apiCostAllocation.summary.allocationDifference : null;
  const unallocatedTruckRest = truckRestPLN !== null && Math.abs(truckRestPLN) >= 0.005 ? truckRestPLN : null;
  const allCargoGoodsValuesKnown = effectiveCargoItems
    .filter((item) => Number(item.quantity || 0) > 0)
    .every(hasKnownGoodsValue);
  const singleOrderOnTruck = route.routeOrders.length + route.supplierOrders.length === 1;
  // Lines the key splits: ZK cargo lines plus the ZD lines this truck really
  // carries (Addendum A `carried`; an older response without it counts every
  // load line). One order with one line → nothing to split.
  const carriedSupplierLineCount = route.supplierOrders
    .flatMap((supplierOrder) => supplierOrder.loadItems || [])
    .filter((item) => (item.carried === undefined ? true : Number(item.carried?.quantity || 0) > 0))
    .length;
  const nothingToSplit = singleOrderOnTruck && effectiveCargoItems.length + carriedSupplierLineCount <= 1;
  // Statistics: at most three compact rows — orders | cost, area | volume, and
  // the load (quantity + weight) across the full width. An unknown volume of a
  // ZD-only truck says so instead of reading 0; weight only shows when known.
  const unknownText = t('transport:supplierOrders.freight.unknown');
  const cargoParts: string[] = [];
  if (totalCargoQuantitySummary && totalCargoQuantitySummary !== '-') cargoParts.push(totalCargoQuantitySummary);
  const formatCurrencyAmount = (value: number | null | undefined): string =>
    value == null ? '—' : formatMoneyPl(value, route.currency);
  // Amounts of „Kalkulacja kosztu dostawy” (see allocationCurrency).
  const formatAllocationAmount = (value: number | null | undefined): string =>
    value == null ? '—' : formatMoneyPl(value, allocationCurrency);
  const statItems: Array<{ key: string; label: string; value: React.ReactNode; title?: string; emphasis?: boolean; wide?: boolean; multiline?: boolean }> = [
    {
      key: 'orders',
      label: t('transport:totalOrders'),
      value: route.routeOrders.length + route.supplierOrders.length,
    },
    // The truck's rate (the former „Koszty” card): in its currency, the PLN
    // equivalent on a ZD-only truck, and the split key when there is a split.
    ...(canViewTransportCosts ? [{
      key: 'cost',
      label: t('transport:cost'),
      value: (
        <>
          {route.transportRate != null ? formatCurrencyAmount(route.transportRate) : (truckTotalPLN === null ? '—' : formatMoneyPl(truckTotalPLN, 'PLN'))}
          {route.invoice ? (
            <span className="transport-detail-stat__sub" data-testid="transport-rate-from-invoice">
              {t('transport:invoice.costFromInvoice', { number: route.invoice.number })}
            </span>
          ) : null}
          {/* A rate (or carrier invoice) in another currency: its PLN
              equivalent from the saved shares — see modules/transports/truckSharesPln.ts. */}
          {ratePLN !== null ? (
            <span className="transport-detail-stat__sub" data-testid="transport-rate-pln">
              {ratePlnText}
            </span>
          ) : null}
          {nothingToSplit ? null : (
            <span className="transport-detail-stat__sub" data-testid="transport-detail-allocation-key">
              {t('transport:allocationKey.short', { key: allocationKeyLabel })}
            </span>
          )}
        </>
      ),
      // Full text of the one-line row (the value is cut with an ellipsis).
      title: [
        route.transportRate != null ? formatCurrencyAmount(route.transportRate) : (truckTotalPLN === null ? '—' : formatMoneyPl(truckTotalPLN, 'PLN')),
        route.invoice ? t('transport:invoice.costFromInvoice', { number: route.invoice.number }) : null,
        ratePLN !== null ? ratePlnText : null,
        nothingToSplit ? null : t('transport:allocationKey.short', { key: allocationKeyLabel }),
      ].filter(Boolean).join(' · '),
      emphasis: true,
      // Amount, PLN with the NBP rate and the key on their own lines — never
      // cut with an ellipsis (review of PR #418 on staging).
      multiline: true,
    }] : []),
    ...(totalAreaSqm > 0 ? [{
      key: 'area',
      label: t('transport:totalArea'),
      value: `${formatMetricNumber(totalAreaSqm, 3)} ${formatUnit('m2')}`,
    }] : []),
    {
      key: 'volume',
      label: t('transport:totalVolume'),
      value: displayVolume === null ? unknownText : `${formatMetricNumber(displayVolume, 3)} ${formatUnit('m3')}`,
    },
  ];
  const routeLabel = buildTransportRouteLabel(
    route.loadingAddresses,
    route.deliveryAddresses,
    route.originCity,
    route.destinationCity,
  );
  const licensePlates = [route.licensePlate1, route.licensePlate2].filter(Boolean).join(', ');
  // Header tiles (process/DESIGN.md): carrier, vehicle and dates in one even
  // row — the former „Trasa i terminy” dates and „Przewoźnik i pojazd” table.
  // One line in a quarter-width tile at 1440 px: the plan only when it is a
  // different day, and without its year when both fall in the same year.
  const actualDeliveryLabel = formatDateEuropean(route.actualDelivery);
  const plannedDeliveryLabel = formatDateEuropean(route.estimatedDelivery);
  const deliveryChipValue = route.actualDelivery
    ? (route.estimatedDelivery && plannedDeliveryLabel !== actualDeliveryLabel
      ? t('transport:detailHeader.deliveredVsPlan', {
        actual: actualDeliveryLabel,
        planned: plannedDeliveryLabel.slice(-4) === actualDeliveryLabel.slice(-4)
          ? plannedDeliveryLabel.slice(0, 5)
          : plannedDeliveryLabel,
      })
      : actualDeliveryLabel)
    : plannedDeliveryLabel;
  // „Ładunek” (inbound truck with ZD): ZD lines → who gets them, one table
  // instead of ZD realization + ZD freight + linked customer orders.
  const showLoadTable = route.direction === 'INBOUND' && route.supplierOrders.length > 0;
  const savedKey = (route.costAllocationMethod || DEFAULT_ALLOCATION_METHOD) as AllocationMethod;
  const shownKey = previewKey ?? savedKey;
  const keyPreviewRoute = showLoadTable && canViewTransportCosts && shownKey !== savedKey
    ? previewRouteWithKey(route, shownKey, zdLineValues)
    : null;
  const loadGroups = showLoadTable ? buildTransportLoadTable(keyPreviewRoute ?? route) : [];
  const freightKeyLabel = (key: AllocationMethod) => (key === DEFAULT_ALLOCATION_METHOD
    ? defaultKeyLabel(defaultKeyLoadsOfRoute(route), route.direction === 'INBOUND', t)
    : t(`transport:${commercialAllocationMethodLabelKey(key)}`));
  // One numbering for „Trasa”, the trailer and the table: the unloading stops.
  const routeDeliveryStops = buildDeliveryStops(route.deliveryAddresses, route.routeOrders);
  const trailerLoad = buildTrailerLoad(loadGroups, deliveryStopNumberByOrderId(routeDeliveryStops));
  // „Ładunek” weighs like the trailer (catalog piece weights) when the trailer is
  // shown, else like the form; a line without a piece weight makes the sum a lower
  // bound („≥”) and no weight at all is „nieznana”, never 0 kg (review 2026-10-06).
  const tileWeight = showLoadTable
    ? { kg: trailerLoad.loadedKg, incomplete: trailerLoad.missingWeightNames.length > 0 }
    : {
      kg: Number(displayWeight) || 0,
      incomplete: effectiveCargoItems.some((item) => Number(item.quantity) > 0 && !(Number(item.grossWeight) > 0)),
    };
  // Whole kilograms, as the trailer writes the same weight.
  if (tileWeight.kg > 0) {
    cargoParts.push(tileWeight.incomplete
      ? t('transport:fields.totalWeightPartial', { weight: formatLocalizedNumber(tileWeight.kg, 0, i18n.language) })
      : `${formatLocalizedNumber(tileWeight.kg, 0, i18n.language)} kg`);
  } else if (tileWeight.incomplete) {
    cargoParts.push(t('transport:fields.loadWeightUnknown'));
  }
  const cargoItem: (typeof statItems)[number] = {
    key: 'cargo',
    label: t('transport:totalCargo'),
    value: cargoParts.length > 0 ? cargoParts.join(' · ') : '—',
    wide: true,
  };
  // „Trasa”: each unloading point with the customer orders unloaded there.
  const deliveryStops = route.routeOrders.length > 0
    ? routeDeliveryStops.map((stop) => ({
      address: stop.address ?? t('transport:linkedOrdersTable.unassignedStop'),
      details: stop.orders.map((order) => (
        <><b>{order.orderNumber}</b>{order.customerName ? ` · ${order.customerName}` : ''}</>
      )),
    }))
    : undefined;
  const headerChips: Array<{ key: string; label: string; value: string }> = [
    { key: 'company', label: t('transport:fields.transportCompany'), value: route.transportCompany || '—' },
    { key: 'contact', label: t('transport:fields.transportContact'), value: route.transportContact || '—' },
    { key: 'plates', label: t('transport:fields.licensePlates'), value: licensePlates || '—' },
    { key: 'driver', label: t('transport:driver'), value: [route.driverName, route.driverPhone].filter(Boolean).join(' · ') || '—' },
    { key: 'loading', label: t('transport:fields.loadingDate'), value: formatDateEuropean(route.loadingDate) },
    {
      key: 'delivery',
      label: route.actualDelivery ? t('transport:fields.actualDelivery') : t('transport:fields.estimatedDelivery'),
      value: deliveryChipValue,
    },
  ];
  // „Więcej” of the header (as on the ZK/ZD card): the main actions stay
  // visible (Wstecz, Zaktualizuj status), the rest sits in one menu.
  const deleteBlockedReason = transportBlockedTooltip('delete');
  const moreMenuItems: NonNullable<MenuProps['items']> = [
    ...(canManageTransports ? [
      { key: 'edit', icon: <EditOutlined />, label: t('common:edit') },
    ] : []),
    ...(canDeleteTransport ? [
      ...(canManageTransports ? [{ type: 'divider' as const }] : []),
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: Boolean(transportBlockedFor('delete')),
        label: <span title={deleteBlockedReason}>{t('transport:messages.deleteButton')}</span>,
      },
    ] : []),
  ];
  const handleMoreMenu = (key: string) => {
    if (key === 'edit') navigate(withTransportReturnContext(`/app/transports/${id}/edit`, returnContext));
    else if (key === 'delete') setDeleteModalVisible(true);
  };
  // Money on this page: formatMoneyPl (utils/currency.ts) — pl-PL grouping,
  // comma decimal, „zł” for PLN like the rest of the app and the ISO code for a
  // foreign currency („4 500,00 zł”, „120,00 EUR”); staging v3.14 still mixed
  // „PLN” and „zł” on the truck card.
  const renderCostAllocationWarning = (
    warning: TransportCostAllocationWarning,
    index: number,
  ) => {
    // Offending product names for MISSING_DIMENSIONS so the operator sees which
    // cargo lines lack dimensions. Unnamed lines are dropped; if none remain we
    // fall back to the generic message with no name suffix.
    const missingDimensionNames =
      ['MISSING_DIMENSIONS', 'MISSING_VALUE', 'COUNTED_IN_PIECES_ONLY'].includes(warning.code)
        ? (warning.items || [])
            .map((item) => (item.name || '').trim())
            .filter((name) => name.length > 0)
            .join(', ')
        : '';

    let description: React.ReactNode;
    if (warning.code === 'MANUAL_SUM_MISMATCH') {
      description = t('transport:commercialAllocationWarningDifference', {
        difference: formatAllocationAmount(costAllocation.summary.allocationDifference),
      });
    } else if (missingDimensionNames) {
      description = t('transport:missingDimensionsItems', { names: missingDimensionNames });
    }

    return (
      <Alert
        key={`${warning.code}-${warning.itemId || index}`}
        type="warning"
        showIcon
        message={t(`transport:${commercialAllocationWarningKey(warning.code)}`)}
        description={description}
        className="transport-detail-allocation-warning"
      />
    );
  };
  const statusDisplay = getTransportStatusDisplay(route.status);
  const railIndex = (RAIL_STEPS as readonly string[]).indexOf(statusDisplay);
  // „Kalkulacja kosztu dostawy”: one row per cargo line. Goods value columns
  // only with sale prices (Logistyk reads the freight columns alone).
  const showAllocationAmounts = Boolean(apiCostAllocation) || hasCompleteCostAllocation;
  // Server panel: a null amount reads "—"; the local fallback keeps
  // "do uzupełnienia" until the whole truck splits.
  const transportMoney = (value: number | null | undefined) => (
    showAllocationAmounts ? formatAllocationAmount(value) : t('transport:commercialAllocationPending')
  );
  const allocationOf = (item: EffectiveCargoRow) => costAllocationItemsById.get(getDetailCargoItemKey(item));
  const amountColumn = (
    key: string,
    titleKey: string,
    value: (item: EffectiveCargoRow) => React.ReactNode,
  ): ColumnsType<EffectiveCargoRow>[number] => ({
    key,
    title: t(`transport:${titleKey}`),
    align: 'right',
    width: key === 'quantity' ? 88 : 104,
    render: (_: unknown, item: EffectiveCargoRow) => <span className="doc-amount">{value(item)}</span>,
  });
  const goodsMoney = (item: EffectiveCargoRow, amount: number | null | undefined) => (
    hasKnownGoodsValue(item) ? formatAllocationAmount(amount) : '—'
  );
  const landedMoney = (item: EffectiveCargoRow, amount: number | null | undefined) => (
    hasKnownGoodsValue(item) ? transportMoney(amount) : '—'
  );
  const cargoCostColumns: ColumnsType<EffectiveCargoRow> = [
    {
      key: 'product',
      title: t('transport:commercialAllocationProduct'),
      render: (_: unknown, item: EffectiveCargoRow) => {
        const dimensions = getOrderProductDisplayDimensions(item);
        return (
          <span className="doc-item" title={dimensions ? `${item.productName} ${dimensions}` : item.productName}>
            <span className="doc-item__name">{item.productName}</span>
            {dimensions ? <span className="doc-item__meta">{dimensions}</span> : null}
          </span>
        );
      },
    },
    amountColumn('quantity', 'commercialAllocationQuantity', (item) => (
      `${formatLocalizedQuantity(Number(item.quantity), i18n.language)} ${formatUnit(item.unit)}`
    )),
    ...(canViewGoodsValue ? [amountColumn('goodsValue', 'commercialAllocationGoodsValue', (item) => (
      goodsMoney(item, allocationOf(item)?.goodsValueTotal ?? item.goodsValueTotal)
    ))] : []),
    amountColumn('allocated', 'commercialAllocationAllocatedTransport', (item) => transportMoney(allocationOf(item)?.allocatedTransportCost)),
    ...(canViewGoodsValue ? [amountColumn('goodsPerUnit', 'commercialAllocationGoodsPerUnit', (item) => (
      goodsMoney(item, allocationOf(item)?.goodsCostPerUnit)
    ))] : []),
    amountColumn('perUnit', 'commercialAllocationTransportPerUnit', (item) => transportMoney(allocationOf(item)?.transportCostPerUnit)),
    amountColumn('perSqm', 'commercialAllocationTransportPerSqm', (item) => transportMoney(allocationOf(item)?.transportCostPerAreaSqm)),
    amountColumn('perCbm', 'commercialAllocationTransportPerCbm', (item) => transportMoney(allocationOf(item)?.transportCostPerVolumeM3)),
    ...(canViewGoodsValue ? [
      amountColumn('totalPerUnit', 'commercialAllocationTotalPerUnit', (item) => landedMoney(item, allocationOf(item)?.landedCostPerUnit)),
      amountColumn('total', 'commercialAllocationTotal', (item) => landedMoney(item, allocationOf(item)?.landedCostTotal)),
    ] : []),
  ];
  // Hero facts: dates, documents and (cost roles) the cost — one line each;
  // the order count and the load stay in „Statystyki”.
  const heroFacts: ObjectCardFact[] = [
    { key: 'loading', label: t('transport:fields.loadingDate'), icon: <CalendarOutlined />, tone: 'blue', value: formatDateEuropean(route.loadingDate), testId: 'transport-detail-fact-loading' },
    {
      key: 'delivery',
      label: route.actualDelivery ? t('transport:fields.actualDelivery') : t('transport:fields.estimatedDelivery'),
      icon: <CheckCircleOutlined />,
      tone: 'green',
      value: deliveryChipValue,
      testId: 'transport-detail-fact-delivery',
    },
    ...(route.warehouseDocuments !== undefined ? [{
      key: 'documents',
      label: t('transport:documents.title'),
      icon: <FileDoneOutlined />,
      tone: 'indigo' as const,
      value: <TransportDocumentLinks documents={route.warehouseDocuments} />,
      testId: 'transport-detail-fact-documents',
    }] : []),
    ...(canViewTransportCosts ? [{
      key: 'cost',
      label: t('transport:cost'),
      icon: <DollarOutlined />,
      tone: 'teal' as const,
      value: route.transportRate != null ? formatCurrencyAmount(route.transportRate) : (truckTotalPLN === null ? '—' : formatMoneyPl(truckTotalPLN, 'PLN')),
      testId: 'transport-detail-fact-cost',
    }] : []),
  ];
  // „Przewoźnik i pojazd”: the former carrier chips, the notes and (for roles
  // without costs) the split key — one Apple-style list.
  const carrierFacts = [
    ...headerChips.filter((chip) => ['company', 'contact', 'plates', 'driver'].includes(chip.key))
      .map((chip) => ({ label: chip.label, value: chip.value })),
    ...(!canViewTransportCosts ? [{ label: t('transport:allocationKey.label'), value: allocationKeyLabel }] : []),
    ...(route.notes ? [{ label: t('transport:fields.notes'), value: route.notes }] : []),
  ];
  const priceChangeNote = (location.state as { transportPriceChange?: { before: string; after: string } } | null)?.transportPriceChange;

  return (
    <div className="obj-card doc-card transport-detail">
      {/* Object-card hero (direction 2026-10-19): truck glyph, kind, number,
          status and kind pills, route and carrier as the meta line, actions
          right (outline first, primary last), one strip of facts; alerts
          inside the hero. */}
      <ObjectCardHero
        testId="transport-detail-header"
        kind={t('transport:transport')}
        name={(
          <Tooltip title={route.displayLabel && route.routeNumber ? t('transport:previousRouteNumber', { number: route.routeNumber }) : undefined}>
            <span>{getTransportLabel(route)}</span>
          </Tooltip>
        )}
        avatar={<CarOutlined />}
        backLabel={t('common:menu.transports')}
        onBack={() => goBack(resolveTransportReturnPath(returnContext, '/app/transports'))}
        badges={(
          <>
            {statusPill(route.status)}
            <TransportKindTag kind={deriveTransportKind({
              direction: route.direction,
              customerOrderCount: route.routeOrders.length,
              supplierOrderCount: route.supplierOrders?.length || 0,
            })} />
          </>
        )}
        meta={[
          <span key="route" title={routeLabel.tooltip}>{routeLabel.label}</span>,
          route.transportCompany || null,
        ]}
        actions={(
          <>
            {moreMenuItems.length > 0 ? (
              <Dropdown menu={{ items: moreMenuItems, onClick: ({ key }) => handleMoreMenu(key) }} trigger={['click']}>
                <Button icon={<MoreOutlined />} data-testid="transport-detail-more">
                  {t('transport:detailHeader.more')}
                </Button>
              </Dropdown>
            ) : null}
            {canManageTransports ? (
              <Button
                type="primary"
                disabled={Boolean(transportBlockedFor('changeStatus'))}
                title={transportBlockedTooltip('changeStatus')}
                onClick={() => setStatusModalVisible(true)}
              >
                {t('transport:updateStatus')}
              </Button>
            ) : null}
          </>
        )}
        facts={heroFacts}
        alerts={priceChangeNote || transportBlockedActions.length > 0 || staleQuantityWarnings.length > 0 ? (
          <>
            {priceChangeNote ? (
              <Alert
                type="info"
                showIcon
                closable
                data-testid="transport-detail-price-change"
                message={t('transport:messages.priceChanged', priceChangeNote)}
              />
            ) : null}
            {transportBlockedActions.length > 0 ? (
              <BlockedActionsAlert blockedActions={transportBlockedActions} ns="transport" reasonKeys={TRANSPORT_ERROR_CODE_KEYS} />
            ) : null}
            {/* HOTEL (HIGH, "warn, never block"): the order changed since this
                transport's cargo was built (e.g. quantity reduced) — non-blocking,
                no auto-resync. */}
            {staleQuantityWarnings.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message={t('transport:cargo.staleQuantityWarningTitle')}
                description={(
                  <ul className="transport-detail-alert-list">
                    {formatStaleCargoQuantityWarningItems(staleQuantityWarnings, t).map((line, index) => (
                      <li key={staleQuantityWarnings[index].orderProductId}>{line}</li>
                    ))}
                  </ul>
                )}
              />
            ) : null}
          </>
        ) : undefined}
      />

      {/* Only the truck has statuses among the object cards: Planowany →
          W realizacji → Zrealizowany; a cancelled truck has no current stage. */}
      <TransportStatusRail
        ariaLabel={t('transport:detailHeader.railLabel')}
        steps={RAIL_STEPS.map((key) => ({ key, label: t(`transport:status.${key}`) }))}
        currentIndex={railIndex < 0 ? null : railIndex}
        closed={statusDisplay === 'completed'}
        interrupted={route.status === 'DELAYED'}
      />

      {/* Two equal columns; sections in one row share their height, a lone
          section or a wide collection spans the row. */}
      <div className="obj-card__body">
        <DetailSection
          title={t('transport:route')}
          icon={<EnvironmentOutlined />}
          tone="teal"
          className="transport-detail-section--route"
        >
          <RouteJourney
            loadingLabel={t('transport:fields.loadingAddresses')}
            deliveryLabel={t('transport:fields.deliveryAddresses')}
            loadingAddresses={route.loadingAddresses}
            deliveryAddresses={route.deliveryAddresses}
            deliveryStops={deliveryStops}
            dates={[]}
          />
        </DetailSection>

        <DetailSection
          title={t('transport:statistics')}
          icon={<BarChartOutlined />}
          tone="indigo"
          className="transport-detail-section--stats"
        >
          <dl className="obj-facts transport-detail-stats" data-testid="transport-detail-stats">
            {[...statItems, cargoItem].map((item) => (
              <div key={item.key} data-testid={`transport-stat-${item.key}`} className={`obj-facts__row transport-detail-stat${item.multiline ? ' transport-detail-stat--multiline' : ''}`}>
                <dt>{item.label}</dt>
                <dd title={item.title ?? (typeof item.value === 'string' ? item.value : undefined)}>{item.value}</dd>
              </div>
            ))}
          </dl>
        </DetailSection>

        <DetailSection
          title={t('transport:detailHeader.carrierSection')}
          icon={<IdcardOutlined />}
          tone="blue"
          className="transport-detail-section--carrier"
          wide={!canViewTransportCosts}
        >
          <ObjectCardFacts items={carrierFacts} />
        </DetailSection>
        {canViewTransportCosts ? (
          <DetailSection
            title={t('transport:invoice.title')}
            icon={<FileTextOutlined />}
            tone="orange"
            className="transport-detail-section--invoice"
            extra={canEditTransportInvoice ? (
              <div className="transport-invoice__actions">
                {route.invoice ? (
                  <Button size="small" danger loading={invoiceRemoving} onClick={handleRemoveInvoice} data-testid="transport-invoice-remove">
                    {t('transport:invoice.remove')}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  onClick={() => setInvoiceDrawerOpen(true)}
                  data-testid="transport-invoice-open"
                >
                  {route.invoice ? t('transport:invoice.edit') : t('transport:invoice.add')}
                </Button>
              </div>
            ) : null}
          >
            <TransportInvoiceSummary invoice={route.invoice} legacyNumber={route.invoiceNumber} exchangeRate={route.freightExchangeRate} />
          </DetailSection>
        ) : null}
        {canEditTransportInvoice ? (
          <TransportInvoiceDrawer
            open={invoiceDrawerOpen}
            transport={route}
            onClose={() => setInvoiceDrawerOpen(false)}
            onSave={handleSaveInvoice}
          />
        ) : null}

        {/* Etap 2 (transport przywozowy): the inbound leg — each ZD's realization
            (Plan / Przyjęto / Nie przyjęto from this truck's PZ), for every role
            that sees the truck. Rendered only when the transport carries ZD. */}
        {route.supplierOrders.length > 0 ? (
          <DetailSection
            title={showLoadTable ? t('transport:loadTable.title') : t('transport:supplierOrders.linkedTitle')}
            icon={<InboxOutlined />}
            tone="blue"
            count={route.supplierOrders.length}
            hint={showLoadTable && canViewTransportCosts ? t('transport:loadTable.hint') : undefined}
            wide
            className="transport-detail-section--supplier-orders"
          >
            {route.supplierOrders.flatMap((supplierOrder) => (supplierOrder.cargoWarnings || [])
              .filter((warning) => warning.code === 'SUPPLIER_CARGO_QUANTITY_MISSING' || warning.code === 'SUPPLIER_CARGO_OVER_ALLOCATED')
              .map((warning, index) => (
                <Alert
                  key={`${supplierOrder.id}-${warning.code}-${index}`}
                  type="warning"
                  showIcon
                  className="transport-detail-alert"
                  data-testid="transport-supplier-cargo-warning"
                  message={warning.code === 'SUPPLIER_CARGO_QUANTITY_MISSING'
                    ? t('transport:supplierCargo.detailMissingQuantity', {
                      number: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
                      trucks: (warning.transportLabels || []).join(', '),
                    })
                    : t('transport:supplierCargo.detailOverAllocated', {
                      number: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
                    })}
                />
              )))}
            {route.supplierOrders.flatMap((supplierOrder) => unassignedSupplierLineMessages(
              supplierOrder.cargoWarnings,
              t,
              i18n.language,
            ).map((message, index) => (
              <Alert
                key={`${supplierOrder.id}-unassigned-${index}`}
                type="info"
                showIcon
                className="transport-detail-alert"
                data-testid="transport-supplier-cargo-line-unassigned"
                message={message}
              />
            )))}
            {showLoadTable ? (
              <>
                {canViewTransportCosts && route.supplierOrders.some((supplierOrder) => !supplierOrder.freightShare) ? (
                  <Alert
                    type="info"
                    showIcon
                    className="transport-detail-alert"
                    data-testid="transport-load-no-share"
                    message={t('transport:supplierOrders.freight.noShare')}
                  />
                ) : null}
                {canViewTransportCosts ? (
                  <TransportFreightKeyBar
                    totalPLN={truckTotalPLN}
                    savedKey={savedKey}
                    shownKey={keyPreviewRoute ? shownKey : savedKey}
                    defaultLabel={defaultKeyLabel(defaultKeyLoadsOfRoute(route), true, t)}
                    availability={route.allocationKeyAvailability}
                    valuesReady={zdLineValues !== null}
                    canSave={canManageTransports && !['DELIVERED', 'CANCELLED'].includes(route.status)}
                    saving={keySaving}
                    onPreview={(key) => setPreviewKey(key === savedKey ? null : key)}
                    onSave={() => void handleSaveFreightKey(shownKey, freightKeyLabel(shownKey))}
                  />
                ) : null}
                <TransportTrailerView load={trailerLoad} showCosts={canViewTransportCosts} />
                {shortDeliveries.length > 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    data-testid="transport-short-deliveries"
                    message={t('transport:loadTable.shortDeliveryTitle')}
                    description={(
                      <ul className="transport-detail-alert-list">
                        {shortDeliveries.map((row) => {
                          const item = route.cargoItems.find((cargo) => cargo.id === row.key);
                          const dimensions = item ? getOrderProductDisplayDimensions(item) : null;
                          return (
                            <li key={row.key}>
                              {t('transport:loadTable.shortDeliveryLine', {
                                product: dimensions ? `${row.productName} ${dimensions}` : row.productName,
                                order: orderNumberById.get(row.orderId) || '—',
                                planned: `${formatLocalizedQuantity(row.planned, i18n.language)} ${formatUnit(row.unit, i18n.language)}`,
                                delivered: `${formatLocalizedQuantity(row.delivered, i18n.language)} ${formatUnit(row.unit, i18n.language)}`,
                              })}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  />
                ) : null}
                <TransportLoadTable
                  groups={loadGroups}
                  toneByOrderId={trailerToneByOrderId(trailerLoad)}
                  stopByOrderId={trailerStopByOrderId(trailerLoad)}
                  showCosts={canViewTransportCosts}
                  onOpenOrder={(orderId) => navigate(`/app/orders/${orderId}?tab=transport`)}
                  onOpenSupplierOrder={(supplierOrderId) => navigate(`/app/supplier-orders/${supplierOrderId}`)}
                  onUnlink={canManageTransports ? (orderId) => void handleRemoveOrder(orderId) : undefined}
                />
              </>
            ) : (
              <div className="transport-detail-card-list" data-testid="transport-linked-supplier-orders">
                {supplierRealization.map((row) => (
                  <SupplierOrderRealizationCard
                    key={row.id}
                    row={row}
                    transportStatus={route.status}
                    onOpenSupplierOrder={() => navigate(`/app/supplier-orders/${row.id}`)}
                  />
                ))}
              </div>
            )}
          </DetailSection>
        ) : null}

        {canViewTransportCosts && !showLoadTable && supplierFreight.rows.length > 0 ? (
          <DetailSection
            title={t('transport:supplierOrders.freight.title')}
            icon={<DollarOutlined />}
            tone="green"
            count={supplierFreight.rows.length}
            hint={t('transport:supplierOrders.freight.hint')}
            wide
            className="transport-detail-section--supplier-freight"
            extra={supplierFreight.rows.length === 1 ? (
              <Tag color="blue" data-testid="transport-supplier-freight-single">
                {t('transport:supplierOrders.freight.singleOrder')}
              </Tag>
            ) : null}
          >
            <SupplierOrderFreightCards
              truck={freightTruck}
              rows={supplierFreight.rows}
              onOpenSupplierOrder={(supplierOrderId) => navigate(`/app/supplier-orders/${supplierOrderId}`)}
            />
          </DetailSection>
        ) : null}

        {canViewTransportCosts && customerFreight.rows.length > 0 ? (
          <DetailSection
            title={t('transport:customerOrders.freight.title')}
            icon={<DollarOutlined />}
            tone="green"
            count={customerFreight.rows.length}
            hint={t('transport:customerOrders.freight.hint')}
            wide
            className="transport-detail-section--customer-freight"
            // Unlike the ZD cards (one ZD = the whole stock share), a legacy mixed
            // truck can leave a single ZK with less than 100 %, so read the percent.
            extra={customerFreight.rows.length === 1 && (customerFreight.rows[0].share.percent ?? 0) >= 99.995 ? (
              <Tag color="blue" data-testid="transport-customer-freight-single">
                {t('transport:customerOrders.freight.singleOrder')}
              </Tag>
            ) : null}
          >
            <CustomerOrderFreightCards
              truck={freightTruck}
              rows={customerFreight.rows}
              onOpenOrder={(orderId) => navigate(`/app/orders/${orderId}?tab=transport`)}
            />
          </DetailSection>
        ) : null}

        {/* A ZD-only truck needs no customer-order block: a WZ "prosto z
            ciężarówki" picks the delivery through the ZD link, and a customer
            order is added from its own ZK card (owner 2026-10-14). */}
        {showLoadTable || (route.routeOrders.length === 0 && route.supplierOrders.length > 0) ? null : (
        <DetailSection
            title={t('transport:linkedOrders')}
            icon={<ShoppingCartOutlined />}
            tone="orange"
            count={route.routeOrders.length}
            wide
            className="transport-detail-section--linked-orders"
          >
          {route.routeOrders.length === 0 ? (
            <ObjectCardEmpty icon={<ShoppingCartOutlined />} text={t('transport:supplierOrders.noCustomerOrders')} />
          ) : (
            <TransportLinkedOrdersTable
              transport={route}
              showCosts={canViewTransportCosts}
              onOpenOrder={(orderId) => navigate(`/app/orders/${orderId}?tab=transport`)}
              onUnlink={canManageTransports ? (orderId) => void handleRemoveOrder(orderId) : undefined}
            />
          )}
        </DetailSection>
        )}

        {/* Per-line freight of customer orders exists only where the truck
            charges them (Magazyn → klient); on an inbound truck it is all
            zeros — its freight is in the ZD batches shown above. */}
        {canViewTransportCosts && route.direction !== 'INBOUND' && (effectiveCargoItems.length > 0 || route.supplierOrders.length === 0) ? (
          <DetailSection
            title={t('transport:commercialAllocationTitle')}
            icon={<CalculatorOutlined />}
            tone="indigo"
            count={effectiveCargoItems.length}
            wide
            className="transport-detail-section--commercial-allocation"
          >
            {/* One table: a row per cargo line, product with its dimensions. */}
            <Table<EffectiveCargoRow>
              size="small"
              rowKey={getDetailCargoItemKey}
              columns={cargoCostColumns}
              dataSource={effectiveCargoItems}
              pagination={false}
              scroll={canViewGoodsValue ? { x: 1040 } : undefined}
            className="transport-detail-cargo-cost-table"
              data-testid="transport-cargo-cost-list"
              onRow={(item) => ({ 'data-testid': `transport-cargo-cost-${getDetailCargoItemKey(item)}` } as React.HTMLAttributes<HTMLElement>)}
            />
            <div className="doc-totals transport-detail-allocation-summary">
              {canViewGoodsValue ? (
                <span>
                  {t('transport:commercialAllocationSummaryGoodsValue', {
                    value: allCargoGoodsValuesKnown ? formatAllocationAmount(costAllocation.summary.totalGoodsValue) : '—',
                  })}
                </span>
              ) : null}
              {hasCompleteCostAllocation ? (
                <>
                  <span>
                    {t('transport:commercialAllocationSummaryTransport', {
                      value: formatAllocationAmount(costAllocation.summary.totalAllocatedTransportCost),
                    })}
                  </span>
                  {unallocatedTruckRest !== null ? (
                    <span data-testid="transport-allocation-truck-rest">
                      {t('transport:commercialAllocationSummaryTruckRest', {
                        value: formatAllocationAmount(unallocatedTruckRest),
                      })}
                    </span>
                  ) : null}
                  {canViewGoodsValue ? (
                    <strong>
                      {t('transport:commercialAllocationSummaryTotal', {
                        value: allCargoGoodsValuesKnown ? formatAllocationAmount(costAllocation.summary.totalLandedCost) : '—',
                      })}
                    </strong>
                  ) : null}
                </>
              ) : null}
            </div>
            {hasCompleteCostAllocation ? null : valueSplitPending ? (
              <Alert type="info" showIcon className="transport-detail-allocation-warning" message={t('transport:allocationKey.valueOnSave')} />
            ) : (
              <Alert
                type="warning"
                showIcon
                className="transport-detail-allocation-warning"
                message={t('transport:commercialAllocationIncompleteTitle')}
                description={t('transport:commercialAllocationIncompleteDescription', {
                  value: formatCurrencyAmount(route.transportRate ?? route.transportCost ?? 0),
                })}
              />
            )}
            {valueSplitPending ? null : visibleCostAllocationWarnings.map(renderCostAllocationWarning)}
          </DetailSection>
        ) : null}

      </div>

      <Modal
        title={t('transport:updateRouteStatus')}
        open={statusModalVisible}
        onCancel={() => {
          statusForm.setFieldsValue({
            status: route.status,
            actualDelivery: route.actualDelivery ? dayjs(route.actualDelivery) : null,
            deliveryItems: buildTransportDeliveryFormItems(route.cargoItems),
            notes: route.notes || undefined,
          });
          setStatusModalVisible(false);
        }}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleStatusUpdate}>
          <Form.Item
            name="status"
            label={t('transport:fields.status')}
            rules={[{ required: true, message: t('transport:pleaseSelectStatus') }]}
          >
            <TransportStatusPicker />
          </Form.Item>
          {editedStatus === 'DELIVERED' ? (
            <>
              <Form.Item
                name="actualDelivery"
                label={t('transport:fields.actualDelivery')}
                rules={[{ required: true, message: t('transport:validation.actualDeliveryRequired') }]}
              >
                <DatePicker
                  placeholder={t('transport:placeholders.selectDate')}
                  {...transportDatePickerProps}
                />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                message={t('transport:actualDeliveryRequiredHint')}
                className="transport-detail-modal-warning"
              />
              <TransportDeliveryQuantityFields
                cargoItems={route.cargoItems}
                routeOrders={route.routeOrders}
              />
            </>
          ) : null}
          <Form.Item name="notes" label={t('transport:notes')}>
            <TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('transport:updateStatus')}
            </Button>
            <Button
              onClick={() => {
                setStatusModalVisible(false);
                statusForm.resetFields();
              }}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={t('transport:messages.deleteButton')}
        open={deleteModalVisible}
        onOk={() => void handleDeleteTransport()}
        onCancel={() => setDeleteModalVisible(false)}
        okButtonProps={{ danger: true, loading: deleting }}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
      >
        <p>{t('transport:messages.deleteConfirmTitle', { number: getTransportLabel(route) })}</p>
      </Modal>
    </div>
  );
};

export default TransportRouteDetail;
